<template>
  <main>
    <section v-if="error" class="error">
      {{ error }}
    </section>

    <modal
      v-if="showModalOverview"
      :show-close="true"
      wrapperClasses="impact-report-modal z-20"
      @close="closeOverviewModal()">
      <div slot="body" class="body my-4">
        <p>
          <span v-if="!showModalNewGroup">{{ memberCount }}</span>
          <span v-else>create a name for your new group,</span>
          <span :class="showModalNewGroup && 'new-group-label'">{{ eventLabel }}</span>
        </p>
        <p v-if="!showModalNewGroup">{{ optedInCount }} are opted in to sms</p>
        <input v-else v-model="newGroupName" maxlength="60" type="text">
        <p v-if="inputValidationError" class="input-error-text">{{ inputValidationError }}</p>
      </div>
      <div slot="action" class="action">
        <template v-if="!showModalNewGroup">
          <div class="grid grid-cols-2 gap-1">
            <button @click="sendMembersToCampaign()" class="button text-base p-2 md:p-3">
              send these members a campaign
            </button>
            <button
              @click="createNewMemberGroup()"
              class="button text-base p-2 md:p-3">
              create a group for these members
            </button>
          </div>
        </template>
        <template v-else>
          <button
            @click="saveNewGroup()"
            :disabled="newGroupName === '' || creatingNewGroup"
            class="button text-base">
            save group
          </button>
        </template>
      </div>
    </modal>

    <div class="action-bar sb-row">
      <section>
        <div class="text-xl mb-3">impact report</div>
      </section>
    </div>

    <div class="flex justify-center">
      <section class="flex flex-col flex-wrap lg:flex-row justify-between w-full max-w-5xl">
        <section class="flex flex-col items-center md:items-start px-4 md:px-10">
          <p class="w-full mb-2 text-sm sm:text-base">select an event and see its impact on behavior</p>
          <sb-async-dropdown
            :rows.sync="eventDropdown.rows"
            :selected.sync="eventDropdown.selected"
            :focused.sync="eventDropdown.focused"
            :options="eventDropdown.options"
            @update:selected="updatedSelectedEvent"
            class="dropdown-component mb-4"
          />

          <sb-date-range
            v-if="selectedInputType === 'date_range'"
            :start.sync="eventRangeMin"
            :end.sync="eventRangeMax"
            placeholder="choose date"
            format="M-d-Y"
            class="w-full mb-4 flex justify-center md:justify-start" />

          <sb-async-dropdown
            v-if="selectedInputType === 'select'"
            :rows.sync="eventOptionsDropdown.rows"
            :selected.sync="eventOptionsDropdown.selected"
            :focused.sync="eventOptionsDropdown.focused"
            :options="eventOptionsDropdown.options"
            class="dropdown-component event-option mb-4"
          >
            <template #label-only="{label}">
              <div class="break-words text-left overflow-hidden">
                {{label}}
              </div>
            </template>
          </sb-async-dropdown>

          <div v-if="selectedInputType === 'integer_range'" class="integer-range-row mb-4">
            <div class="report-row">
              <input
                type="number"
                name="event_range_min"
                class="sb"
                v-model="eventRangeMin"
                placeholder="min"
                min="0"
                @keydown="validateIntegerRange"
                @focus="clearInputError" />
              <div class="split">to</div>
              <input
                type="number"
                name="event_range_max"
                class="sb"
                v-model="eventRangeMax"
                placeholder="max"
                :min="eventRangeMin + 1"
                @keydown="validateIntegerRange"
                @focus="clearInputError" />
            </div>
            <p v-if="inputValidationError" class="input-error-text">{{ inputValidationError }}</p>
          </div>

          <div v-if="selectedInputType === 'time_range'" class="integer-range-row w-full mb-4">
            <div class="report-row">
              <sb-date :flatpickr="{ onClose: [updateTimeRangeMin, checkMinMaxTime] }" :value.sync="eventRangeMin" :include="['time']" format="hh:mm aa" class="sb-white-input flex-1" placeholder="start time"></sb-date>
              <div class="split">to</div>
              <sb-date :flatpickr="{ onClose: [updateTimeRangeMax, checkMinMaxTime] }" :value.sync="eventRangeMax" :include="['time']" format="hh:mm aa" class="sb-white-input flex-1" placeholder="end time"></sb-date>
            </div>
            <p v-if="inputTimeValidationError" class="input-error-text">{{ inputTimeValidationError }}</p>
          </div>

          <div v-if="selectedInputType === 'text' || selectedInputType === 'integer'" class="w-full">
            <input
              type="text"
              class="sb w-full mb-2"
              name="event_value"
              v-model="eventValue"
              :placeholder="inputTextIntegerPlaceholder"
              @input="validateTextIntegerInput"
              @keypress="validateNonInteger"
              @focus="clearInputError"
              :class="{ 'input-error': inputValidationError }" />
              <p v-if="inputValidationError" class="input-error-text">{{ inputValidationError }}</p>
          </div>
        </section>

        <section class="flex flex-col items-start px-4 md:px-10">
          <p class="w-full mb-2 text-sm sm:text-base">select KPIs you wish to see</p>
          <sb-async-dropdown
            :rows="kpisDropdown.rows"
            :selected.sync="kpisDropdown.selected"
            :focused.sync="kpisDropdown.focused"
            @selectedRow="kpisDropdown.selectedRow"
            @deselectedRow="kpisDropdown.deselectedRow"
            :options="kpisDropdown.options"
            class="dropdown-component mb-4"
          />
        </section>

        <section class="flex flex-col items-start px-4 md:px-10">
          <div @click="showGroupDropdown = !showGroupDropdown" class="w-full cursor-pointer mb-2 text-center md:text-left">
            <span v-if="!showGroupDropdown" class="conditions-label">+</span>
            <span v-if="showGroupDropdown">-</span>
            <span :class="{'conditions-label': !showGroupDropdown}">add conditions to the customers who did the event (optional)</span>
          </div>

          <recipient-dropdown
            v-if="showGroupDropdown"
            :groups-with-selected="groupsWithSelected"
            :show-subtext="false"
            hide-preview
            showDeselectAll
            @update:selected="setSelectedGroups"
            class="dropdown-component border-none-important w-full mb-4"
          >
            <div v-if="totalSelectedCustomerFilter < 1">
              select customer conditions
            </div>
            <div v-else-if="totalSelectedCustomerFilter === 1">
              conditions (1 selected)
            </div>
            <div v-else-if="totalSelectedCustomerFilter > 1">
              multiple
            </div>
          </recipient-dropdown>
        </section>
      </section>
    </div>

    <div class="flex justify-center">
      <section class="flex justify-end w-full max-w-5xl py-4 px-4 md:px-10">
        <button
        class="button text-base px-8 py-3 w-full md:w-auto"
        type="submit"
        @click="initiateReportJob"
        :disabled="disableRunReport"
        >
          see impact
          <sb-fai class="text-md ml-1" :icon="['fas', 'chevron-right']" />
        </button>
        <p v-if="reportGenerationError" class="input-error-text generate-report-error">{{ reportGenerationError }}</p>
      </section>
    </div>

    <section v-if="waitingForReport" class="loading-container">
      <h3>impact report in progress</h3>
      <p>estimated time remaining: {{ loadTimeRemaining }}</p>
      <sb-loading-icon />
    </section>

    <section v-if="fetchedReport" class="report-container">
      <div class="report-divider my-6"></div>
      <div class="reports-header">
        <div v-if="Object.keys(reportData).length > 0" class="report-info">
          <p class="text-center md:text-left"><span>event:</span><br class="block md:hidden" /> {{ reportHeaderEvents }} </p>
          <p class="text-center md:text-left" v-if="reportHeaderMemberConditions !== ''"><span>member conditions:</span><br class="block md:hidden" /> {{ reportHeaderMemberConditions }}</p>
          <p class="text-center md:text-left"><span>date created:</span><br class="block md:hidden" /> {{ reportHeaderDateCreated }}</p>
        </div>
        <div class="download-actions text-center md:text-left">
          <a
            v-if="showGenerateDownload"
            @click="generateReportDownload()">
            generate download
          </a>
          <p v-if="showDownloadLoading">
            generating download... should be ready within a few minutes
          </p>
          <a
            v-if="showDownloadReport"
            :href="downloadReportUrl"
            download>
            download report
          </a>
          <p v-if="reportExportError" class="input-error-text report-export-error">{{ reportExportError }}</p>
        </div>
      </div>
      <div>
        <div class="hidden md:block">
          <sb-2d-table
            class="impact-report"
            :columns="columns"
            :rows="rows"
            :cells="cells"
          >
            <template #column="{ column }">
              <a @click="columnHeaderClick(column.key, column.label)">{{ generateLabel(column.label) }}</a>
            </template>
            <template #cell="{ row, column }">
              <span class="cell" :class="{ 'with-secondary': cells[row.key][column.key].secondary }">
                <div class="primary">{{cells[row.key][column.key].primary}}</div>
                <div class="secondary">{{cells[row.key][column.key].secondary}}</div>
              </span>
            </template>
          </sb-2d-table>
        </div>
        <swipe-pagination
          v-if="columns"
          class="swipe-pagination-container block md:hidden"
          :columns="columns.map(column => ({
            label: column.key,
            content: column.label,
          }))"
        >
          <template #swipe-pagination-content="{ column }">
            <div class="w-full flex flex-col justify-center">
              <a class="text-2xl text-center px-12" @click="columnHeaderClick(column.label, column.content)">{{ generateLabel(column.content) }}</a>
              <div class="flex flex-col items-center w-full gap-0 mt-5" v-for="(row, index) of rows" :key="index">
                <div class="text-center px-10 w-2/5">{{row.label}}</div>
                <div class="p-1 w-2/5 border border-solid border-primary rounded-xl" :class="{ 'py-4': !cells[row.key][column.label].secondary }">
                  <div class="text-center text-2xl">{{cells[row.key][column.label].primary}}</div>
                  <div class="text-center">{{cells[row.key][column.label].secondary}}</div>
                </div>
              </div>
            </div>
          </template>
        </swipe-pagination>
        <button
          class="delete-button text-base"
          @click="deleteReportModalVisible = true"
        >
          delete
        </button>
      </div>
    </section>

    <delete-report-modal
      v-if="deleteReportModalVisible"
      :reportId="reportData.id"
      @close="deleteReportModalVisible = false"
    />
  </main>
</template>

<script>
import { reportsAPI } from "../../../services/api/reportsAPI";
import { pluralize } from "../../../utilities";
import { axios } from "../../../../src/config/axios";
import { groupsAPI } from "../../../services/api/groupsAPI";
import { mapState } from "vuex";
import { format, utcToZonedTime } from "date-fns-tz";
import { parse, parseISO, isValid } from "date-fns"
import Modal from '../../../components/shared/Modal';
import DeleteReportModal from "./DeleteReportModal";
import moment from 'moment-timezone';
import RecipientDropdown from '../../../components/Campaigns/recipient-dropdown.vue';
import { SwipePagination } from '@springbig/sb-components';

export default {
  name: 'ImpactReportNew',

  components: {
    Modal,
    DeleteReportModal,
    RecipientDropdown,
    SwipePagination,
  },

  data() {
    return {
      columns: [],
      rows: [],
      cells: {},
      eventValue: '',
      eventRangeMin: null,
      eventRangeMax: null,
      reportId: null,
      downloadReportId: null,
      waitingForReport: false,
      fetchedReport: false,
      error: null,
      groupsWithSelected: null,
      inputValidationError: null,
      inputTimeValidationError: null,
      reportGenerationError: null,
      reportExportError: null,
      existingReportFetched: false,
      dropdownsPopulated: false,
      reportData: {},
      showGenerateDownload: true,
      showDownloadLoading: false,
      showDownloadReport: false,
      downloadReportUrl: null,
      showModalOverview: false,
      showModalNewGroup: false,
      sendToCampaign: false,
      newGroupName: '',
      creatingNewGroup: false,
      variant: null,
      eventLabel: '',

      timeFormat: 'h:mm aaa',
      calendarFormat: 'M/d/Y',

      memberCount: null,
      optedInCount: '',

      eventDropdown: {
        rows: ['loading-row'],
        selected: null,
        focused: false,
        options: {
          label: (selected) => selected ? selected.label?.toLowerCase() : 'select an event'
        }
      },

      eventsOptionsLabels: {
        'brand_purchased': 'brand',
        'category_purchased': 'category',
        'purchase_on_day_of_week': 'day of week',
        'reward_redeemed': 'reward',
        'offer_redeemed': 'offer',
      },

      eventOptionsDropdown: {
        rows: ['loading-row'],
        selected: null,
        focused: false,
        options: {
          label: (selected) => selected ? selected.label?.toLowerCase() : 'select '+this.eventsOptionsLabels[this.eventDropdown.selected?.key]
        }
      },

      showGroupDropdown: false,

      kpisDropdown: {
        path: '',
        pathIndex: new Map().set('', new Set()),
        rows: ['loading-row'],
        selected: [],
        focused: false,
        options: {
          label: (selected) => {
            return `select kpis (${selected.length} selected)`
          }
        },
        selectedRow: state => {
          this.kpisDropdown.pathIndex.get(this.kpisDropdown.path).add(state.id);
        },
        deselectedRow: state => {
          this.kpisDropdown.pathIndex.get(this.kpisDropdown.path).delete(state.id);
        }
      },

      cache: new Map(),

      downloadReportTimeout: null,

      deleteReportModalVisible: false,
      merchantSegmentIds: [],
      merchantTagIds: [],
      merchantTags: {}
    }
  },

  async created() {
    this.$store.dispatch('LOAD_CURRENT_MERCHANT')
    await this.fetchKpis();
    await this.fetchEventTypes();

    const groups = await groupsAPI.getGroups();

    if(groups) this.groupsWithSelected = groups;
    // fetch report if id is present; new/show pages use same vue component
    if (this.$route.params.id) {
      this.existingReportFetched = true
      this.waitingForReport = true
      this.reportId = this.$route.params.id
      this.downloadReportId = this.$route.params.id
      this.pollReport()
    }
  },

  watch: {
    'eventDropdown.selected'(newEvent) {
      this.reset();

      if(newEvent.input_type === "time_range") {
        this.eventRangeMin = new Date();
        this.eventRangeMin.setHours(0,0,0,0);

        this.eventRangeMax = new Date();
        this.eventRangeMax.setHours(23,59,59,999);
      }
    }
  },

  computed: {
    ...mapState(['currentMerchant']),
    selectedInputType() {
      return this.eventDropdown.selected?.input_type
    },
    disableRunReport() {
      if (this.waitingForReport || !this.eventDropdown.selected) {
        return true
      }

      if (this.kpisDropdown.selected.length < 1) {
        return true
      }

      if (this.eventDropdown.selected.key === 'purchase_within_hours_of_day_range') {
        return false
      }

      if (['date_range', 'integer_range', 'time_range'].includes(this.selectedInputType)) {
        return !this.eventRangeMin || !this.eventRangeMax
      }

      return !this.eventValue && !this.eventOptionsDropdown.selected
    },
    inputTextIntegerPlaceholder() {
      if (this.eventDropdown.selected.key === 'redemption_count_achieved') {
        return 'min count 1'
      }

      return ''
    },
    loadTimeRemaining() {
      if (this.reportData.estimated_run_time_remaining !== undefined) {
        const secondsToMinutes = Math.ceil(this.reportData.estimated_run_time_remaining / 60)
        const minutesRemaining = secondsToMinutes < 1 ? 1 : secondsToMinutes

        return `${ minutesRemaining } ${ pluralize(minutesRemaining, 'minute') }`
      }

      return 'estimating...'
    },
    reportHeaderEvents() {
      const minMaxValues = `${this.reportData.input_data_labels.event_range_min} - ${this.reportData.input_data_labels.event_range_max}`
      const hasEventValue = this.reportData.input_data.event_value || this.reportData.input_data.event_value === 0
      const eventValue = hasEventValue ? this.reportData.input_data_labels.event_value : minMaxValues
      return `${this.reportData.input_data_labels.event_type?.toLowerCase()} - ${this.generateLabel(eventValue)}`
    },
    reportHeaderMemberConditions() {
      return this.reportData.selected_member_filters.join('; ').toLowerCase()
    },
    reportHeaderDateCreated() {
      const utcToLocalDate = utcToZonedTime(new Date(this.reportData.created_at), this.currentMerchant.merchant_time_zone)
      return format(utcToLocalDate, `${this.calendarFormat} ${this.timeFormat}`)
    },
    totalSelectedCustomerFilter(){
      return this.merchantSegmentIds.length + this.merchantTagIds.length;
    }
  },

  methods: {
    async fetchEventTypes() {
      const rows = await reportsAPI.fetchImpactReportEventTypes()

      this.eventDropdown.rows = rows.map((row) => {
        return {
          slot: 'select-row',
          state: row
        }
      })
    },
    async fetchKpis() {
      const rows = await reportsAPI.fetchKpis()

      this.kpisDropdown.rows = rows.map((row, index) => {
        const formattedState = {
          ...row,
          id: index
        }

        if (formattedState.selected) {
          this.kpisDropdown.selected.push(formattedState)
        }

        return {
          slot: 'toggle-row',
          state: formattedState
        }
      })
    },

    async populateDropdowns() {
      const data = this.reportData.input_data

      // populate event dropdown + selected value if applicable
      this.eventDropdown.selected = this.eventDropdown.rows.find(({ state }) => state.key === data.event_type).state || null
      if (this.eventDropdown.selected) {
        await this.updatedSelectedEvent()

        if (this.eventDropdown.selected.input_type === 'select') {
          this.eventOptionsDropdown.selected = this.eventOptionsDropdown.rows.find(({ state }) => state.key === data.event_value)?.state || null
        }

        if (['date_range', 'integer_range'].includes(this.selectedInputType)) {
          const isDateRange = this.selectedInputType === 'date_range'

          this.eventRangeMin = isDateRange ? parseISO(data.event_range_min) : data.event_range_min
          this.eventRangeMax = isDateRange ? parseISO(data.event_range_max) : data.event_range_max
        }

        if (this.selectedInputType === 'time_range') {
          const minTime = this.reportData.input_data.event_range_min
          const maxTime = this.reportData.input_data.event_range_max

          // check if valid date (old report format) or just a time (new report format) and parse date accordingly
          this.eventRangeMin = isValid(parseISO(minTime)) ? utcToZonedTime(new Date(minTime), this.currentMerchant.merchant_time_zone) : parse(minTime, 'p', new Date())
          this.eventRangeMax = isValid(parseISO(maxTime)) ? utcToZonedTime(new Date(maxTime), this.currentMerchant.merchant_time_zone) : parse(maxTime, 'p', new Date())
        }

        if (['text', 'integer'].includes(this.selectedInputType)) {
          this.eventValue = data.event_value
        }
      }

      // populate customer conditions dropdown
      if (this.reportData.selected_member_filters.length > 0) {
        // add selected merchant segments / tags to dropdown
        await this.addFiltersToRecipientDropdown(this.reportData)
      }

      // populate kpi dropdown for existing reports
      const allKpisSelected = this.kpisDropdown.rows.length === data.selected_assessments?.length

      if (data.selected_assessments && !allKpisSelected) {
        // if kpi is selected but not showing, select
        data.selected_assessments.forEach(kpi => {
          const kpiRef = this.kpisDropdown.rows.find(({ state }) => state.key === kpi).state || null
          const kpiExistsInSelected = this.kpisDropdown.selected.find(x => x.key === kpi)

          if (kpiRef && !kpiExistsInSelected) {
            this.kpisDropdown.selected.push(kpiRef)
            this.kpisDropdown.selectedRow(kpiRef)
          }
        })

        // if kpi is not selected but showing, deselect
        const kpisNotSelected = this.kpisDropdown.selected.filter(item => !data.selected_assessments.find(x => x === item.key))

        if (kpisNotSelected.length > 0) {
          kpisNotSelected.forEach(kpi => {
            const kpiIndex = this.kpisDropdown.selected.findIndex(x => x.key === kpi.key)
            this.kpisDropdown.selected.splice(kpiIndex, 1)
            this.kpisDropdown.deselectedRow(kpi)
          })
        }
      } else if (!data.selected_assessments || allKpisSelected) {
        // if kpi data doesnt exist || all selected, select all
        this.kpisDropdown.rows.forEach(({ state }) => {
          if (!this.kpisDropdown.selected.find(x => x.key === state.key)) {
            this.kpisDropdown.selected.push(state)
            this.kpisDropdown.selectedRow(state)
          }
        })
      }
    },

    async addFiltersToRecipientDropdown(reportData) {
      for(const group of this.groupsWithSelected.groups_with_selected) {
        if(reportData.selected_merchant_segment_keys[group.id]){
          // 1000 is number of elements per page
          // since we don't know on which page element is present
          const data = await groupsAPI.getGroupChildren(group, 1, '', 1000);
          group.display_names = data.children.filter(child => {
                                  if(reportData.selected_merchant_segment_keys[group.id].includes(child.id)) {
                                    this.merchantSegmentIds.push(child.id);
                                    return child
                                  }
                                }).map(group => group.name);
          group.merchant_segment_ids = reportData.selected_merchant_segment_keys[group.id];
        }
        if(reportData.selected_merchant_tag_keys[group.context]){
          // 1000 is number of elements per page
          // since we don't know on which page element is present
          const data = await groupsAPI.getGroupChildren(group, 1, '', 1000);
          group.display_names = data.children.filter(child => {
                                  if(reportData.selected_merchant_tag_keys[group.context].includes(child.id)) {
                                    this.merchantTags[group.context] ? this.merchantTags[group.context].push(child.id) : this.merchantTags[group.context] = [child.id];
                                    this.merchantTagIds.push(child.id);
                                    return child
                                  }
                                }).map(group => group.name);
          group.tags = data.children.map(tag => tag.tag);
        }
      }
    },

    async updateTimeRangeMin(selectedDates) {
      this.eventRangeMin = selectedDates[0];
    },
    async updateTimeRangeMax(selectedDates) {
      this.eventRangeMax = selectedDates[0];
    },
    async checkMinMaxTime() {
      this.validateTimeInputs()
    },

    async generateReportDownload() {
      this.showGenerateDownload = false
      this.showDownloadLoading = true
      this.reportExportError = null

      await reportsAPI.generateReport(this.downloadReportId)
      this.generateReport()
    },
    generateReport() {
      this.downloadReportTimeout = setTimeout(() => {
        reportsAPI.fetchReport(this.downloadReportId)
          .then((data) => {
            if (data.report.download_url) {
              this.downloadReportUrl = data.report.download_url
              this.showDownloadLoading = false
              this.showDownloadReport = true
            } else if (data.report.export_state === 'failed') {
              this.resetDownloadUI()
              this.reportExportError = 'report export failed. please try again.';
            } else {
              this.generateReport()
            }
          }).catch(this.handleError)
      }, 5000)
    },
    resetDownloadUI() {
      this.showGenerateDownload = true
      this.showDownloadLoading = false
      this.showDownloadReport = false
      this.downloadReportUrl = null
    },
    columnHeaderClick(key, label) {
      this.variant = key !== 'never' ? 'qualified' : 'unqualified'
      this.optedInCount = this.cells.members ? this.cells.members[key].formatted_opt_in_count : 'n/a'
      this.memberCount = this.cells.members ? this.cells.members[key].primary : 'n/a'
      this.eventLabel = label.toLowerCase()
      this.showModalOverview = true
      this.clearInputError()
    },
    sendMembersToCampaign() {
      this.sendToCampaign = true
      this.createNewMemberGroup()
    },
    createNewMemberGroup() {
      this.newGroupName = '';
      this.showModalNewGroup = true
    },
    closeOverviewModal() {
      this.sendToCampaign = false
      this.showModalNewGroup = false
      this.showModalOverview = false
      this.clearInputError()
    },
    async saveNewGroup() {
      this.creatingNewGroup = true

      try {
        const newSegment = await reportsAPI.createSegment(this.reportId || this.reportData.id, this.variant, encodeURIComponent(this.newGroupName));

        if (this.sendToCampaign) {
          const campaignId = await reportsAPI.createCampaign(newSegment.merchant_segment.id)
          window.location.pathname = `/campaigns/${campaignId}/edit`
        } else {
          this.showModalOverview = false
          this.showModalNewGroup = false
          this.newGroupName = ''
        }
      } catch (err) {
        if (err.response.data.error) {
          this.inputValidationError = err.response.data.error;
        } else {
          this.handleError(err);
        }
      } finally {
        this.creatingNewGroup = false
      }
    },
    async updatedSelectedEvent() {
      this.eventOptionsDropdown.selected = null;

      try {
        if (!this.eventDropdown.selected.data_url) return

        const resp = await axios.get(this.eventDropdown.selected.data_url);

        this.eventOptionsDropdown.rows = resp.data.map((row) => {
          return {
            slot: 'select-row',
            state: row
          }
        });
      } catch(err) {
        this.handleError(err);
      }
    },

    validateTextIntegerInput(event) {
      const integerKeys = ['redemption_count_achieved', 'spend_total_achieved', 'visit_count_achieved'];

      if (integerKeys.includes(this.eventDropdown.selected.key) && isNaN(event.target.value)) {
        this.eventValue = '';
        this.inputValidationError = 'must be a number';
      } else if (integerKeys.includes(this.eventDropdown.selected.key) && this.eventDropdown.selected.key !== 'visit_count_achieved' && event.target.value < 1) {
        this.eventValue = '';
        this.inputValidationError = 'must be greater than zero';
      } else if (this.inputValidationError) {
        this.clearInputError();
      }
    },

    validateNonInteger(event) {
      if (['redemption_count_achieved', 'visit_count_achieved'].includes(this.eventDropdown.selected.key) && event.key === '.') {
        event.preventDefault()
      }
    },

    validateIntegerRange(event) {
      if (event.key === '-') {
        event.preventDefault()
      }
    },

    validateReportInputs() {
      if (parseInt(this.eventRangeMin) > parseInt(this.eventRangeMax)) {
        this.inputValidationError = 'max range must be larger than min range';
      } else {
        this.inputValidationError = null;
      }
    },

    validateTimeInputs() {
      if (Math.round(this.eventRangeMin.getTime() / 1000) > Math.round(this.eventRangeMax.getTime() / 1000)) {
        this.inputTimeValidationError = "hey, 'start' time of the provided interval is later than the 'end' time. please update the time range of the report.";
      } else {
        this.inputTimeValidationError = null;
      }
    },

    setSelectedGroups(frag) {
      if(frag.merchant_segment_ids){
        this.merchantSegmentIds = frag.merchant_segment_ids;
      }
      if(frag.merchant_tags){
        this.merchantTags = frag.merchant_tags;
      }
      if(frag.merchant_tag_ids){
        this.merchantTagIds = frag.merchant_tag_ids;
      }
    },

    clearInputError() {
      this.inputValidationError = null;
    },

    reset() {
      this.error = null;
      this.eventRangeMin = null;
      this.eventRangeMax = null;
      this.eventValue = '';
      this.reportId = null;
      this.eventOptions = [];
      this.waitingForReport = false;
      this.inputValidationError = null;
      this.reportGenerationError = null;
      this.reportExportError = null;
    },

    formatMerchantLocalTimezoneToUTC(date) {
      const zonedTime = moment.tz(moment(date).format('YYYY-MM-DD HH:mm:ss'), this.currentMerchant.merchant_time_zone)
      return zonedTime.utc();
    },

    async initiateReportJob() {
      // if download report is running, clear settimeout if running a new report
      if (this.downloadReportTimeout) {
        clearTimeout(this.downloadReportTimeout)
      }

      this.validateReportInputs()

      if (this.inputValidationError) return

      this.waitingForReport = true;
      this.fetchedReport = false;
      this.reportGenerationError = null;
      this.reportExportError = null;

      let formattedMinDate
      let formattedMaxDate

      // purchase within calendar range, send only date to BE
      if (this.selectedInputType === 'date_range') {
        formattedMinDate = format(this.eventRangeMin, 'yyyy-MM-dd')
        formattedMaxDate = format(this.eventRangeMax, 'yyyy-MM-dd')
      }

      // purchase within hours, send only time to BE
      if (this.selectedInputType === 'time_range') {
        formattedMinDate = format(this.eventRangeMin, 'p')
        formattedMaxDate = format(this.eventRangeMax, 'p')
      }

      try {
        const reportData = await reportsAPI.postImpactReportJob({
          report: {
            type: 'Reports::ImpactReport',
            input_data: {
              event_type: this.eventDropdown.selected.key,
              event_value: (this.selectedInputType === 'select' ? this.eventOptionsDropdown.selected.key : this.eventValue),
              event_range_min: formattedMinDate || this.eventRangeMin,
              event_range_max: formattedMaxDate || this.eventRangeMax,
              member_filters: {
                merchant_segment_ids: this.merchantSegmentIds,
                tags: this.merchantTags
              },
              selected_assessments: this.kpisDropdown.selected.map(x => x.key)
            }
          }
        });

        this.reportId = reportData.id
        this.downloadReportId = reportData.id
        setTimeout(() => {
          this.pollReport();
        }, 1000);
      } catch(err) {
        this.handleError(err);
      }
    },

    async pollReport() {
      const data = await reportsAPI.fetchReport(this.reportId);

      if(data) {
        this.reportData = data.report

        if (data.report.state === 'completed') {
          // if loading an existing report, populate dropdowns - run only once
          if (this.existingReportFetched && !this.dropdownsPopulated) {
            await this.populateDropdowns()
            this.showGroupDropdown = true;
            this.dropdownsPopulated = true
          }

          if (this.reportData.download_url) {
            this.downloadReportUrl = this.reportData.download_url
            this.showGenerateDownload = false
            this.showDownloadReport = true
          } else {
            this.resetDownloadUI()
          }

          this.setTableData(data.report.output_data);
          this.waitingForReport = false;
          this.fetchedReport = true;
          this.existingReportFetched = false

          // update url to reflect 'show' state
          window.history.replaceState(null, '', `/reports/impact/${data.report.id}`)
        } else if(data.report.state === 'failed') {
          this.reportGenerationError = 'report generation failed. please try again.';
          this.waitingForReport = false;
        } else {
          setTimeout(() => {
            this.pollReport();
          }, 1000);
        }
      } else{
        this.handleError()
      }
    },

    setTableData(data) {
      const rowsAndCells = data.rows.reduce((res, row) => {
        const key = row.label?.toLowerCase().replace(' ', '_');
        res.rows.push({
          label: row.label,
          key
        });

        res.cells[key] = row.values;

        return res;
      }, {
        rows: [],
        cells: {}
      });

      // member_count is always returned by BE. if !selected, filter out
      if (!this.reportData.input_data.selected_assessments.find(x => x === 'member_count')) {
        rowsAndCells.rows = rowsAndCells.rows.filter(x => x.key !== 'members')
      }

      this.columns = data.cols;
      this.rows = rowsAndCells.rows;
      this.cells = rowsAndCells.cells;
    },

    handleError(e) {
      this.error = e;
      this.waitingForReport = false;
    },

    generateLabel(label) {
      return label?.toLowerCase() || 'n/a'
    },
  }
}
</script>

<style scoped>
button,
.button {
  font-family: "AileronThin";
}

.border-none-important {
  border: none !important;
}
.dropdown-component {
  min-width: 300px;
}
.dropdown-component >>> main.label-container {
  border-radius: 0.5rem;
}
.dropdown-component >>> .sb-async-dropdown {
  margin: 0;
}
.dropdown-component.focused >>> main.label-container,
.dropdown-component >>> .sb-async-dropdown.focused .label-container main.label-container {
  border-radius: 0.5rem 0.5rem 0 0;
}
.dropdown-component.focused >>> .sb-content,
.dropdown-component >>> .sb-async-dropdown.focused .sb-content {
  border-radius: 0 0 0.5rem 0.5rem;
}

.dropdown-component.event-option >>> main.label-container {
  border: 1px solid #128ff9;
  color: #128ff9;
  background-color: #fff;
  padding: 1rem;
  font-family: AileronLight, sans-serif;
  align-items: normal;
}

.dropdown-component.event-option >>> main.label-container > div {
  justify-content: space-between;
}

.dropdown-component.event-option >>> .sb-content {
  border-top: 0;
}

.dropdown-component.event-option.focused >>> main.label-container {
  border-bottom: 0;
}

.dropdown-component >>> .dropdown-item {
  text-transform: lowercase;
}

.dropdown-component >>> .select-row {
  word-break: break-word;
}

.input-error {
  border-color: #f91253 !important;
}

.input-error-text {
  color: #f91253;
  margin: -8px 0 0;
}

.generate-report-error,
.report-export-error {
  margin: 16px 0 0 !important;
}

.integer-range-row .input-error-text {
  margin-top: 0;
}

.impact-report {
  column-gap: 10px;
  align-items: center;
}

.sb-dropdown, .sb-date-range {
  width: 100%;
  margin-bottom: 15px;
}

.report-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between
}

.report-row .sb, .report-row .sb-input {
  flex-grow: 1;
  margin: 0;
}

.report-row .split {
  margin: 0 20px;
}

.action-bar {
  text-align: right;
  margin: 20px 0;
  max-width: 1440px;
}

.sb-2d-table .cell {
  min-width: 150px;
}

.sb-2d-table .cell.with-secondary {
  padding: 4px 25px;
}

.sb-2d-table .cell.with-secondary .primary {
  line-height: 18px;
}

.sb-2d-table .cell.with-secondary .secondary {
  line-height: 14px;
  font-size: 0.9em;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.sb-loading-icon {
  width: 100%;
  max-width: 200px;
}

.reports-header {
  margin-bottom: 38px;
}

.report-info p {
  margin-bottom: 0;
}

.report-info span {
  font-weight: bold;
}

.download-actions p {
  margin: 0;
}

.download-actions a {
  font-weight: bold;
}

.impact-report-modal p {
  text-align: center;
  margin: 0 auto;
  max-width: 360px !important;
}

.impact-report-modal input {
  max-width: 250px;
  margin: 16px auto 0;
}

.impact-report-modal .action {
  text-align: center;
}

.impact-report-modal button {
  max-width: 180px;
}

.report-divider {
  height: 2px;
  background: #128ff9;
}

.new-group-label {
  font-weight: bold;
}

.conditions-label {
  color: #128ff9;
}

.delete-button {
  float: right;
  color: grey;
  margin-top: 50px;
  cursor: pointer;
}

button,
a,
div {
  -webkit-tap-highlight-color:transparent;
}

@media only screen and (max-width: 1023px) {
  .dropdown-component {
    width: 100%;
    min-width: unset;
    z-index: 9;
  }
  .recipient-dropdown >>> .sb-async-dropdown.focused {
    min-width: unset;
  }
  .dropdown-component.focused >>> .sb-content,
  .dropdown-component >>> .sb-async-dropdown.focused .sb-content {
    z-index: 9;
    position: relative;
  }
  .dropdown-component >>> .sb-async-dropdown.focused .sb-content .sb-content-items {
    max-width: unset;
  }
  .dropdown-component >>> main.label-container {
    padding: 2rem 0.5rem;
  }
  .sb-dropdown, .sb-date-range >>> .flatpickr-wrapper {
    width: 70%;
  }
  .sb-dropdown, .sb-date-range >>> .flatpickr-wrapper .flatpickr-calendar {
    left: -20px;
  }
}
</style>
